/**
 * Company Profile Page Component
 * Displays company details and activity history
 * 
 * Refactored: Components and hooks extracted to separate modules
 */
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, AlertTriangle } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { ResponsiveLayout } from "../../components/layout";

// Import modular components
import { 
  CompanyInfoCard,
  AssignAgentModal, 
  EditCompanyModal, 
  DeleteConfirmModal, 
  MeetingOutcomeModal,
  EmailOutcomeModal,
  ActivityHistory,
  CompanyTargetListPills
} from "./components";

// Import custom hooks
import { useCompanyData, useCompanyActions, useMeetingOutcome } from "./hooks";

const CompanyProfilePage = () => {
  const { companyId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();

  // Role checks
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';
  const isTeamLeader = user?.role === 'team_leader';
  const isAdmin = user?.role === 'admin';
  const canAssignAgents = isManagerOrAdmin || isTeamLeader;

  // Data fetching hook
  const { company, activities, loading, agents, refetch } = useCompanyData(
    companyId, token, canAssignAgents, navigate
  );

  // Company actions hook
  const {
    assigning, saving, deleting,
    handleAssign, handleUnassign, handleSaveEdit, 
    handleDeleteCompany, handleDeleteActivity
  } = useCompanyActions(companyId, token, refetch, navigate);

  // Meeting outcome hook
  const meetingOutcome = useMeetingOutcome(token, refetch);

  // Email outcome modal state
  const [emailOutcomeActivity, setEmailOutcomeActivity] = useState(null);
  const handleEmailOutcomeClick = (activity) => setEmailOutcomeActivity(activity);

  // Modal states
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showDeleteCompanyModal, setShowDeleteCompanyModal] = useState(false);
  const [showDeleteActivityModal, setShowDeleteActivityModal] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);

  // Edit modal handler
  const handleEditClick = () => {
    setEditForm({
      name: company?.name || "",
      city: company?.city || "",
      industry: company?.industry || "",
      oib: company?.oib || "",
      address: company?.address || "",
      director_name: company?.director_name || "",
      phone: company?.phone || "",
      email: company?.email || ""
    });
    setShowEditModal(true);
  };

  // Delete activity confirmation
  const confirmDeleteActivity = (activity) => {
    setActivityToDelete(activity);
    setShowDeleteActivityModal(true);
  };

  if (loading) {
    return (
      <div className="mobile-container flex items-center justify-center lg:ml-[280px] lg:min-h-screen">
        <div className="text-gray-500">Učitavanje...</div>
      </div>
    );
  }

  return (
    <ResponsiveLayout title={company?.name || "Firma"} showBack onBack={() => navigate(-1)}>
      <div className={isDesktop ? "max-w-4xl" : "pb-32"}>
        {isDesktop ? (
          /* Desktop: Two column layout */
          <div className="grid grid-cols-3 gap-6">
            {/* Left: Company Info */}
            <div className="col-span-1">
              <CompanyTargetListPills companyId={companyId} />
              {company?.needs_contact_review && (
                <div
                  data-testid="needs-contact-review-banner-desktop"
                  className="mb-3 bg-amber-50 border border-amber-300 rounded-xl p-3 flex items-start gap-2 text-sm text-amber-900"
                >
                  <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Provjeri kontakt podatke</div>
                    <div className="text-xs">
                      {company.contact_review_reason === "EMAIL_BOUNCE"
                        ? "E-mail nije dostavljen (bounce). Provjerite adresu."
                        : "Kontakt podaci zahtijevaju provjeru."}
                    </div>
                  </div>
                </div>
              )}
              <CompanyInfoCard
                company={company}
                canAssignAgents={canAssignAgents}
                isAdmin={isAdmin}
                onAssignClick={() => setShowAssignModal(true)}
                onUnassign={handleUnassign}
                onEditClick={handleEditClick}
                onDeleteClick={() => setShowDeleteCompanyModal(true)}
                assigning={assigning}
              />
            </div>
            
            {/* Right: Activity History */}
            <div className="col-span-2">
              <ActivityHistory
                activities={activities}
                companyId={companyId}
                isDesktop={isDesktop}
                isAdmin={isAdmin}
                onOutcomeClick={meetingOutcome.handleOutcomeClick}
                onEmailOutcomeClick={handleEmailOutcomeClick}
                onDeleteClick={confirmDeleteActivity}
              />
            </div>
          </div>
        ) : (
          /* Mobile: Stacked layout */
          <>
            <div className="space-y-4">
              {/* Company Info Card */}
              <CompanyTargetListPills companyId={companyId} />
              {company?.needs_contact_review && (
                <div
                  data-testid="needs-contact-review-banner"
                  className="bg-amber-50 border border-amber-300 rounded-xl p-3 flex items-start gap-2 text-sm text-amber-900"
                >
                  <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Provjeri kontakt podatke</div>
                    <div className="text-xs">
                      {company.contact_review_reason === "EMAIL_BOUNCE"
                        ? "E-mail nije dostavljen (bounce). Provjerite je li adresa točna."
                        : "Kontakt podaci zahtijevaju provjeru."}
                    </div>
                  </div>
                </div>
              )}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden animate-fadeIn">
                <div className="p-4">
                  <CompanyInfoCard
                    company={company}
                    canAssignAgents={canAssignAgents}
                    isAdmin={isAdmin}
                    onAssignClick={() => setShowAssignModal(true)}
                    onUnassign={handleUnassign}
                    onEditClick={handleEditClick}
                    onDeleteClick={() => setShowDeleteCompanyModal(true)}
                    assigning={assigning}
                  />
                </div>
              </div>
              
              {/* Activity History */}
              <ActivityHistory
                activities={activities}
                companyId={companyId}
                isDesktop={isDesktop}
                isAdmin={isAdmin}
                onOutcomeClick={meetingOutcome.handleOutcomeClick}
                onEmailOutcomeClick={handleEmailOutcomeClick}
                onDeleteClick={confirmDeleteActivity}
              />
            </div>

            {/* Fixed Bottom CTA - Mobile Only */}
            <div className="fixed bottom-24 left-1/2 -translate-x-1/2 w-[calc(100%-40px)] max-w-[408px] z-40">
              <button
                onClick={() => navigate(`/company/${companyId}/activity/new`)}
                data-testid="add-activity-button"
                className="h-12 w-full bg-[#1B3F8F] hover:bg-[#153275] text-white font-medium rounded-xl transition-all active:scale-[0.98] shadow-lg shadow-blue-900/20 flex items-center justify-center gap-2"
              >
                <Plus size={20} />
                Dodaj aktivnost
              </button>
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      <AssignAgentModal
        show={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        agents={agents}
        selectedAgentId={selectedAgentId}
        setSelectedAgentId={setSelectedAgentId}
        onAssign={() => handleAssign(selectedAgentId, () => {
          setShowAssignModal(false);
          setSelectedAgentId("");
        })}
        assigning={assigning}
      />

      <EditCompanyModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        editForm={editForm}
        setEditForm={setEditForm}
        onSave={() => handleSaveEdit(editForm, () => setShowEditModal(false))}
        saving={saving}
      />

      <DeleteConfirmModal
        show={showDeleteCompanyModal}
        onClose={() => setShowDeleteCompanyModal(false)}
        title="Obriši firmu"
        message={`Jeste li sigurni da želite obrisati firmu "${company?.name}"? Ova akcija je nepovratna i obrisat će sve povezane aktivnosti.`}
        onConfirm={() => handleDeleteCompany(company?.name, () => setShowDeleteCompanyModal(false))}
        deleting={deleting}
      />

      <DeleteConfirmModal
        show={showDeleteActivityModal}
        onClose={() => {
          setShowDeleteActivityModal(false);
          setActivityToDelete(null);
        }}
        title="Obriši aktivnost"
        message="Jeste li sigurni da želite obrisati ovu aktivnost? Ova akcija je nepovratna."
        onConfirm={() => handleDeleteActivity(activityToDelete?.id, () => {
          setShowDeleteActivityModal(false);
          setActivityToDelete(null);
        })}
        deleting={deleting}
      />

      <MeetingOutcomeModal
        show={meetingOutcome.showOutcomeModal}
        onClose={() => meetingOutcome.setShowOutcomeModal(false)}
        selectedMeeting={meetingOutcome.selectedMeeting}
        meetingOutcome={meetingOutcome.meetingOutcome}
        setMeetingOutcome={meetingOutcome.setMeetingOutcome}
        outcomeNote={meetingOutcome.outcomeNote}
        setOutcomeNote={meetingOutcome.setOutcomeNote}
        nextStep={meetingOutcome.nextStep}
        setNextStep={meetingOutcome.setNextStep}
        nextActionDate={meetingOutcome.nextActionDate}
        setNextActionDate={meetingOutcome.setNextActionDate}
        rescheduledDateTime={meetingOutcome.rescheduledDateTime}
        setRescheduledDateTime={meetingOutcome.setRescheduledDateTime}
        lostReason={meetingOutcome.lostReason}
        setLostReason={meetingOutcome.setLostReason}
        savingOutcome={meetingOutcome.savingOutcome}
        onSave={meetingOutcome.handleSaveOutcome}
        company={company}
        backofficeEmail={meetingOutcome.backofficeEmail}
      />

      <EmailOutcomeModal
        open={!!emailOutcomeActivity}
        activity={emailOutcomeActivity}
        onClose={() => setEmailOutcomeActivity(null)}
        onSaved={() => { setEmailOutcomeActivity(null); refetch(); }}
      />
    </ResponsiveLayout>
  );
};

export default CompanyProfilePage;
